// Real-user (field) Core Web Vitals measurement — task #7086.
//
// Google Search Console reports mobile URLs failing LCP/INP from *field*
// (CrUX) data, but until now we had zero field measurement of our own, so we
// could neither see which routes/interactions are slow for real visitors nor
// verify recovery after a fix. This module captures LCP, INP and CLS (plus
// the attribution detail that makes them actionable: route, device class,
// LCP element / INP target) and reports them to PostHog as `web_vitals`
// events.
//
// Consent model: identical to PostHog pageview capture. Events are sent via
// the shared analytics state's PostHog instance, which
//   • runs in memory-persistence (cookieless) mode pre-consent,
//   • upgrades to durable persistence on analytics consent,
//   • is fully opted out on explicit reject (`opt_out_capturing`) — capture()
//     is then a no-op.
// No separate consent logic is needed here; we piggy-back on that instance.
//
// Payloads are intentionally small (one event per metric per page, final
// value reported on pageleave/visibility-hidden by the web-vitals library).
// A page emits at most ~3 events, so no sampling is applied today; if volume
// ever warrants it, set WEB_VITALS_SAMPLE_RATE below 1.

import { onCLS, onINP, onLCP, type MetricWithAttribution } from "web-vitals/attribution";

// 1 = capture every page load. Lower (e.g. 0.25) to sample if event volume
// becomes a cost concern. The decision is made once per page load so a page's
// LCP/INP/CLS always arrive together or not at all.
const WEB_VITALS_SAMPLE_RATE = 1;

export type WebVitalCapture = (props: Record<string, unknown>) => void;

function deviceClass(): "mobile" | "tablet" | "desktop" {
  try {
    const w = window.innerWidth;
    if (w < 768) return "mobile";
    if (w < 1024) return "tablet";
    return "desktop";
  } catch {
    return "desktop";
  }
}

// Route template-ish path: strip the numeric car id and long slugs down so
// events group by template while keeping the real path for drill-down.
function routeTemplate(path: string): string {
  return path
    .replace(/\/cars\/\d+\/[a-z0-9-]+/i, "/cars/:id/:slug")
    .replace(/\/\d+(?=\/|$)/g, "/:n");
}

function baseProps(metric: MetricWithAttribution): Record<string, unknown> {
  const path = window.location.pathname;
  return {
    metric_name: metric.name,
    metric_value: Math.round(metric.value * 1000) / 1000,
    metric_rating: metric.rating, // "good" | "needs-improvement" | "poor"
    metric_delta: Math.round(metric.delta * 1000) / 1000,
    metric_id: metric.id,
    navigation_type: metric.navigationType,
    path,
    route_template: routeTemplate(path),
    device_class: deviceClass(),
  };
}

function trim(s: unknown, max = 200): string | undefined {
  if (typeof s !== "string" || !s) return undefined;
  return s.length > max ? s.slice(0, max) : s;
}

/**
 * Register the web-vitals observers. Call once, as early as possible (the
 * library uses buffered PerformanceObservers, so registering during analytics
 * init still sees the page's LCP). `capture` receives the flattened event
 * properties and is responsible for actually sending (see analytics/index.ts
 * captureWebVital — routes through the shared PostHog instance / queue).
 */
export function initWebVitals(capture: WebVitalCapture): void {
  if (typeof window === "undefined") return;
  if (Math.random() >= WEB_VITALS_SAMPLE_RATE) return;

  const send = (metric: MetricWithAttribution, extra: Record<string, unknown>) => {
    try {
      const props: Record<string, unknown> = { ...baseProps(metric) };
      for (const [k, v] of Object.entries(extra)) {
        if (v !== undefined && v !== null && v !== "") props[k] = v;
      }
      capture(props);
    } catch {
      /* RUM must never break the page */
    }
  };

  try {
    onLCP((m) => {
      const a = m.attribution;
      send(m, {
        lcp_element: trim(a.target),
        lcp_url: trim(a.url),
        lcp_ttfb_ms: a.timeToFirstByte != null ? Math.round(a.timeToFirstByte) : undefined,
        lcp_load_delay_ms: a.resourceLoadDelay != null ? Math.round(a.resourceLoadDelay) : undefined,
        lcp_load_duration_ms: a.resourceLoadDuration != null ? Math.round(a.resourceLoadDuration) : undefined,
        lcp_render_delay_ms: a.elementRenderDelay != null ? Math.round(a.elementRenderDelay) : undefined,
      });
    });
    onINP((m) => {
      const a = m.attribution;
      send(m, {
        inp_target: trim(a.interactionTarget),
        inp_type: a.interactionType,
        inp_load_state: a.loadState,
        inp_input_delay_ms: a.inputDelay != null ? Math.round(a.inputDelay) : undefined,
        inp_processing_ms: a.processingDuration != null ? Math.round(a.processingDuration) : undefined,
        inp_presentation_delay_ms: a.presentationDelay != null ? Math.round(a.presentationDelay) : undefined,
      });
    });
    onCLS((m) => {
      const a = m.attribution;
      send(m, {
        cls_largest_shift_target: trim(a.largestShiftTarget),
        cls_largest_shift_value: a.largestShiftValue != null ? Math.round(a.largestShiftValue * 10000) / 10000 : undefined,
        cls_load_state: a.loadState,
      });
    });
  } catch {
    /* older browsers without the underlying PerformanceObserver types */
  }
}
